import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { TrendingUp, Plus, X, DollarSign, Users, Percent } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { nb, nok } from "@/lib/format";

const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-4))", "hsl(var(--chart-2))", "hsl(var(--chart-5))", "hsl(var(--chart-3))"];

let _uid = 0;
const newInvestor = () => ({ key: ++_uid, mode: "existing", shareholder_id: "", name: "", amount: "" });

function compactNok(n) {
  if (!n || isNaN(n)) return "0 kr";
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toLocaleString("nb-NO", { maximumFractionDigits: 2 })} mill kr`;
  return `${nb(Math.round(n))} kr`;
}

export default function EmissionSim() {
  const { companyId } = useParams();
  const [cap, setCap] = useState(null);
  const [preMoney, setPreMoney] = useState("");
  const [override, setOverride] = useState("");
  const [investors, setInvestors] = useState([newInvestor()]);

  useEffect(() => {
    setCap(null);
    setPreMoney("");
    setOverride("");
    setInvestors([newInvestor()]);
    api.get(`/companies/${companyId}/cap-table`).then((r) => setCap(r.data));
  }, [companyId]);

  const rows = useMemo(() => cap?.rows || [], [cap]);
  const preShares = cap?.total_shares || 0;

  const sim = useMemo(() => {
    const pm = parseFloat(preMoney) || 0;
    const totalInvestment = investors.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
    const ov = parseInt(override, 10) || 0;
    let pricePerShare = 0;
    if (ov > 0) pricePerShare = totalInvestment > 0 ? totalInvestment / ov : 0;
    else if (pm > 0 && preShares > 0) pricePerShare = pm / preShares;

    const invShares = investors.map((i) => {
      const amt = parseFloat(i.amount) || 0;
      const sh = pricePerShare > 0 ? Math.round(amt / pricePerShare) : 0;
      return { ...i, amt, sh };
    });
    const issued = invShares.reduce((s, i) => s + i.sh, 0);
    const newShares = ov > 0 ? ov : issued;
    const postMoney = pm + totalInvestment;
    const newTotal = preShares + issued;

    const before = rows.map((r) => ({ name: r.name, shares: r.shares, pct: preShares ? (r.shares / preShares) * 100 : 0, isNew: false }));
    const afterMap = {};
    rows.forEach((r) => { afterMap[r.id] = { name: r.name, shares: r.shares, isNew: false }; });
    invShares.forEach((i) => {
      if (i.mode === "existing" && i.shareholder_id && afterMap[i.shareholder_id]) {
        afterMap[i.shareholder_id].shares += i.sh;
      } else if (i.sh > 0) {
        const key = `new-${i.key}`;
        const nm = (i.name || "").trim() || "Ny investor";
        afterMap[key] = { name: nm, shares: (afterMap[key]?.shares || 0) + i.sh, isNew: true };
      }
    });
    const after = Object.values(afterMap).map((a) => ({ ...a, pct: newTotal ? (a.shares / newTotal) * 100 : 0 }));
    return { pm, totalInvestment, pricePerShare, newShares, postMoney, newTotal, before, after };
  }, [preMoney, override, investors, rows, preShares]);

  const setInv = (key, patch) => setInvestors((xs) => xs.map((i) => (i.key === key ? { ...i, ...patch } : i)));

  if (!cap) {
    return (
      <div className="mx-auto max-w-6xl">
        <PageHeader title="Simulering" description="Modeller en emisjon og se hvordan eierskapet endres." />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl" data-testid="emission-sim">
      <PageHeader title="Simulering" description="Modeller en emisjon (kapitalforhøyelse) og se hvordan eierskapet fortynnes – før du gjennomfører den." />

      <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-sm">
        <h2 className="font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">Simuler emisjon</h2>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div>
            <Label>Pre-money verdi (NOK)</Label>
            <Input data-testid="premoney-input" type="number" className="mt-1.5" value={preMoney} onChange={(e) => setPreMoney(e.target.value)} placeholder="f.eks. 12000000" />
          </div>
          <div>
            <Label>Antall nye aksjer (valgfri)</Label>
            <Input data-testid="newshares-input" type="number" className="mt-1.5" value={override} onChange={(e) => setOverride(e.target.value)} placeholder="La stå tom for automatisk beregning" />
            <p className="mt-1 text-xs text-muted-foreground">La stå tom for automatisk beregning fra pre-money.</p>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <h3 className="text-sm font-semibold">Investorer i runden</h3>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setInvestors((xs) => [...xs, newInvestor()])} data-testid="add-investor-btn"><Plus className="h-4 w-4" /> Legg til investor</Button>
        </div>
        <div className="mt-3 space-y-3">
          {investors.map((inv) => (
            <div key={inv.key} className="rounded-lg border border-border/60 p-3" data-testid={`investor-row-${inv.key}`}>
              <div className="flex items-center gap-2">
                <div className="flex gap-1 rounded-md border border-border p-0.5">
                  {[["existing", "Eksisterende"], ["new", "Ny"]].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => setInv(inv.key, { mode: v })}
                      className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${inv.mode === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}>{l}</button>
                  ))}
                </div>
                {investors.length > 1 ? (
                  <button onClick={() => setInvestors((xs) => xs.filter((x) => x.key !== inv.key))} className="ml-auto text-muted-foreground transition-colors hover:text-destructive" data-testid={`remove-investor-${inv.key}`}><X className="h-4 w-4" /></button>
                ) : null}
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {inv.mode === "existing" ? (
                  <Select value={inv.shareholder_id} onValueChange={(v) => setInv(inv.key, { shareholder_id: v })}>
                    <SelectTrigger data-testid={`investor-existing-${inv.key}`}><SelectValue placeholder="Velg aksjonær" /></SelectTrigger>
                    <SelectContent>
                      {rows.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input data-testid={`investor-name-${inv.key}`} placeholder="Navn på ny investor" value={inv.name} onChange={(e) => setInv(inv.key, { name: e.target.value })} />
                )}
                <Input data-testid={`investor-amount-${inv.key}`} type="number" placeholder="Investering (NOK)" value={inv.amount} onChange={(e) => setInv(inv.key, { amount: e.target.value })} />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-lg bg-muted/50 p-3 text-sm">
          <span className="text-muted-foreground">Total investering: </span>
          <span className="font-bold" data-testid="total-investment">{compactNok(sim.totalInvestment)}</span>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi icon={TrendingUp} label="Pre-money" value={compactNok(sim.pm)} testid="kpi-premoney" />
        <Kpi icon={DollarSign} label="Post-money" value={compactNok(sim.postMoney)} accent testid="kpi-postmoney" />
        <Kpi icon={Users} label="Nye aksjer" value={nb(sim.newShares)} testid="kpi-newshares" />
        <Kpi icon={Percent} label="Pris per aksje" value={sim.pricePerShare ? nok(Math.round(sim.pricePerShare)) : "–"} testid="kpi-price" />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <OwnershipCard title="Før emisjon" data={sim.before} testid="pie-before" />
        <OwnershipCard title="Etter emisjon" data={sim.after} testid="pie-after" showNew />
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className={`rounded-xl border p-5 ${accent ? "border-primary/30 bg-primary/[0.04]" : "border-border/60 bg-card"}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Icon className="h-4 w-4" /> {label}</div>
      <p className="mt-2 font-headings text-2xl font-extrabold tracking-tight">{value}</p>
    </div>
  );
}

function OwnershipCard({ title, data, showNew, testid }) {
  const sorted = [...data].filter((d) => d.shares > 0).sort((a, b) => b.shares - a.shares);
  return (
    <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-sm" data-testid={testid}>
      <h3 className="font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">{title}</h3>
      <div className="mt-2 h-52">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={sorted} dataKey="shares" nameKey="name" innerRadius={55} outerRadius={82} paddingAngle={2} stroke="none">
              {sorted.map((d, i) => <Cell key={i} fill={d.isNew ? "hsl(var(--lime))" : COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v, n, p) => [`${nb(v)} aksjer (${p.payload.pct.toFixed(1)}%)`, n]}
              contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--popover))", color: "hsl(var(--popover-foreground))", fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 space-y-2">
        {sorted.map((d, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: d.isNew ? "hsl(var(--lime))" : COLORS[i % COLORS.length] }} />
            <span className="min-w-0 flex-1 truncate">{d.name}{d.isNew && showNew ? <span className="ml-1.5 rounded bg-lime px-1.5 text-[10px] font-bold text-lime-foreground">NY</span> : null}</span>
            <span className="font-medium">{d.pct.toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}
